import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { NavigationService } from '../../../navigation/navigation.service';
import { IIcon } from '../../../shared/components/icon/icon.interface';
import { IconSizesEnum } from '../../../shared/components/icon/icons-sizes.enum';
import { FormControl } from '@angular/forms';
import { NotificationDataStore } from '../../../notification-data/notification-data.store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { List } from 'immutable';
import {
  Notification,
  NotificationStateEnum,
} from '../../../notification-data/notification.model';
import { AccountDataStore } from '../../../account-data/account-data.store';
import { User } from '../../../account-data/user.model';
import { SettingsDataStore } from '../../../settings-data/settings-data.store';
import { Settings } from '../../../settings-data/settings.model';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  map,
  pluck,
  shareReplay,
  take,
  takeUntil,
} from 'rxjs/operators';
import { Account } from '../../../account-data/account.model';
import { AuthDataDispatcher } from '../../../auth-data/auth-data.dispatcher';
import { I2gDialogService, I2gLayoutService } from 'i2g-ng-ui';
import { ChangeEmailDialogComponent } from '../../../shared/components/change-email-dialog/change-email-dialog.component';
import { TrackingScreenNamesEnum } from '../../../tracking/tracking.data';
import { SearchDataStore } from '../../../search-data/search-data.store';
import { SearchDataDispatcher } from '../../../search-data/search-data.dispatcher';
import { isEqual, noop } from 'lodash';
import { ISearchParams } from '../../../search-data/search-data.data';
import {
  GLOBAL_SEARCH_ROUTE,
  HOME_MASTER_ROUTE,
  SETTINGS_V2_MASTER_ROUTE,
  SETTINGS_V2_ROUTE,
} from '../../../main/main.data';
import { IDeepLinkProperties } from '../../../navigation/deep-link-properties.interface';
import { NavigationBuilder } from '../../../navigation/navigation.builder';
import { Store } from '@ngrx/store';
import { NotificationDataDispatcher } from '../../../notification-data/notification-data.dispatcher';
import { GlobalBannerService } from '../../../shared/components/global-banner/global-banner.service';
import {
  I2gNgTrackingActionBuilder,
  ITrackingAction,
  ITrackingObject,
} from 'i2g-ng-tracking';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import { TrackingDispatcher } from '../../../tracking/tracking.dispatcher';
import { NavigationExtras } from '@angular/router';

@Component({
  selector: 'app-global-header-v2',
  templateUrl: 'global-header-v2.component.html',
  styleUrls: ['global-header-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalHeaderV2Component implements OnInit, OnDestroy {
  @Output() clickHeaderIcon: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('profileDialog', { static: false }) profileDialog: ElementRef;
  @ViewChild('profileButton', { static: true }) profileButton: ElementRef;

  private _destroyed: Subject<boolean> = new Subject<boolean>();

  routeData$: BehaviorSubject<any>;
  trackingScreenName$: Observable<TrackingScreenNamesEnum>;
  trackingObject: ITrackingObject;
  notificationsTrackingObject: ITrackingObject;
  searchTapped$: Observable<ITrackingAction>;
  notifications$: Observable<List<Notification>>;
  newNotificationsCount$: Observable<number>;
  user$: Observable<User>;
  account$: Observable<Account>;
  companyName$: Observable<string>;
  searchControl: FormControl;
  isProfileDialogOpen = false;
  isNotificationsTrayOpen = false;

  searchIcon: IIcon = {
    name: 'search-v2',
    size: IconSizesEnum.medium,
  };
  i2gIcon: IIcon = {
    name: 'invoice2go-logo-white',
    size: IconSizesEnum.custom,
  };
  hideSideNavigationIa$: Observable<boolean>;
  homeRoute = HOME_MASTER_ROUTE;

  constructor(
    private _navigationService: NavigationService,
    private _notificationDataStore: NotificationDataStore,
    private _accountDataStore: AccountDataStore,
    private _settingsDataStore: SettingsDataStore,
    private _authDataDispatcher: AuthDataDispatcher,
    private _dialogService: I2gDialogService,
    private _searchDataDispatcher: SearchDataDispatcher,
    private _searchDataStore: SearchDataStore,
    private _navigationBuilder: NavigationBuilder,
    private _notificationDataDispatcher: NotificationDataDispatcher,
    private _globalBannerService: GlobalBannerService,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingDispatcher: TrackingDispatcher,
    private _layoutService: I2gLayoutService,
    private _store: Store<any>
  ) {}

  ngOnInit() {
    this.searchControl = new FormControl('', []);

    this.trackingScreenName$ = this._navigationService.activeRouteName$;

    this.hideSideNavigationIa$ = this._navigationService.routeData$.pipe(
      pluck<any, boolean>('hideSideNavigationIa'),
      shareReplay(1)
    );

    this.trackingObject = this._trackingObjectBuilder.buildTopNavigation();
    this.notificationsTrackingObject = this._trackingObjectBuilder.buildNotificationsV2();

    this.searchTapped$ = this._navigationService.activeRouteName$.pipe(
      map((routeName) =>
        this._trackingActionBuilder.buildFieldTapped('search', routeName)
      )
    );

    this._notificationDataDispatcher.registerNotificationsPolling();

    this.notifications$ = this._notificationDataStore
      .getNotifications()
      .pipe(shareReplay(1));
    this.newNotificationsCount$ = this.notifications$.pipe(
      shareReplay(1),
      map(
        (notifications) =>
          notifications.filter((notification) => notification.isNew()).size
      )
    );
    this.user$ = this._accountDataStore.getUser().pipe(shareReplay(1));
    this.account$ = this._accountDataStore.getAccount();
    this.companyName$ = this._settingsDataStore.settings$.pipe(
      map((settings: Settings): string => settings.content.companyInfo.name),
      shareReplay(1)
    );

    this.searchControl.valueChanges
      .pipe(debounceTime(500), takeUntil(this._destroyed))
      .subscribe((value) => this.search(value));

    this._searchDataStore
      .getGlobalSearchParams()
      .pipe(
        distinctUntilChanged((a, b) => isEqual(a, b)),
        map((params: ISearchParams) => (params && params.query) || ''),
        takeUntil(this._destroyed)
      )
      .subscribe((value) => {
        this.searchControl.setValue(value, { emitEvent: false });
      });

    this._navigationService.navigationEnds$
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => {
        this.isNotificationsTrayOpen = false;
      });
  }

  search(term: string) {
    if (!term) {
      this._navigationService.goToByUrl(
        this.getReturnUrl() || HOME_MASTER_ROUTE
      );
      return;
    }

    this._searchDataDispatcher.searchGlobally(term, true);

    const queryParams = {
      query: term,
      returnUrl:
        this.getReturnUrl() || this._navigationService.getLocationPathname(),
    };
    const globalSearchPath = `/${this.getActivePathRoot()}/${GLOBAL_SEARCH_ROUTE}`;

    if (
      this._navigationService.getLocationPathname().startsWith(globalSearchPath)
    ) {
      this._navigationService.goTo([], { queryParams });
    } else {
      this._navigationService.goTo(globalSearchPath, {
        queryParams,
      });
    }
  }

  getReturnUrl(): string {
    return this._navigationService.getLocationQueryParams()['returnUrl'];
  }

  getSearchSourceRoot(): string {
    const searchSource = this.getReturnUrl();
    return (
      searchSource &&
      this._navigationService.getPathSegments(searchSource)[0].path
    );
  }

  getActivePathRoot() {
    return this._navigationService.getPathSegments(
      this._navigationService.getLocationPathname()
    )[0].path;
  }

  onAccountAndSettingsClicked() {
    this._trackButtonEvent('account_profile');

    const isInSettings =
      this._navigationService.getLocationPathname() ===
      SETTINGS_V2_MASTER_ROUTE;
    const navigationExtras: NavigationExtras = isInSettings
      ? { queryParamsHandling: 'preserve' }
      : {
          queryParams: { returnUrl: this._navigationService.getCurrentUrl() },
        };

    this._navigationService.goTo(SETTINGS_V2_ROUTE, navigationExtras);
    this.isProfileDialogOpen = false;
  }

  changeEmail() {
    this._trackButtonEvent('change_email');

    this.isProfileDialogOpen = false;
    this._dialogService.openDialog(ChangeEmailDialogComponent, {
      trackingScreenName: this._navigationService.activeRouteName,
    });
  }

  toggleProfileDialog() {
    this.isProfileDialogOpen = !this.isProfileDialogOpen;

    this._trackButtonEvent('account', {
      action: this.isProfileDialogOpen ? 'opening' : 'closing',
    });
  }

  toggleNotificationsTray() {
    this.isNotificationsTrayOpen = !this.isNotificationsTrayOpen;

    this._trackButtonEvent('notifications', {
      action: this.isNotificationsTrayOpen ? 'opening' : 'closing',
    });

    if (this.isNotificationsTrayOpen) {
      this._layoutService.disableBodyScrolling();

      this.notifications$
        .pipe(take(1), delay(5000), takeUntil(this._destroyed))
        .subscribe((notifications: List<Notification>) => {
          const notificationIds = notifications
            .filter(
              (notification) => notification.state === NotificationStateEnum.new
            )
            .map((notification) => notification.id) as List<string>;

          this._notificationDataDispatcher.batchMarkNotificationsAsSeen(
            notificationIds
          );
        });
    } else {
      this._layoutService.enableBodyScrolling();
    }
  }

  onClickNotification(notification: Notification) {
    const deepLinkProperties: IDeepLinkProperties = this._navigationBuilder.buildDeepLinkProperties(
      notification.actionUrl
    );
    const action: any = this._navigationBuilder.buildActionFromDeeplinkProperties(
      deepLinkProperties
    );

    if (action) {
      this._store.dispatch(action);
    }

    this._notificationDataDispatcher.markNotificationAsRead(notification);
  }

  onDismissNotification(notification: Notification) {
    this._notificationDataDispatcher
      .dismissNotification(notification)
      .subscribe(noop, (error) =>
        this._globalBannerService.showSystemErrorBanner(error)
      );
  }

  logout() {
    this._trackButtonEvent('log_out');

    this._authDataDispatcher.logout();
  }

  @HostListener('document:click', ['$event'])
  dismissProfileDialog(event) {
    if (
      (this.isProfileDialogOpen &&
        !this.profileDialog.nativeElement.contains(event.target)) ||
      this.profileButton.nativeElement.contains(event.target)
    ) {
      this.toggleProfileDialog();
    }
  }

  private _trackButtonEvent(
    identifier: string,
    additionalProperties: any = {}
  ) {
    this._trackingDispatcher.trackEvent(
      this.trackingObject,
      this._trackingActionBuilder.buildButtonTapped(
        identifier,
        this._navigationService.activeRouteName
      ),
      additionalProperties
    );
  }

  ngOnDestroy() {
    this._notificationDataDispatcher.unregisterNotificationsPolling();

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
